import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { approveClaim, rejectClaim } from './useClaims';

/**
 * Hook for fetching all users (admin)
 */
export const useAllUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setUsers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching users:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { users, loading };
};

/**
 * Hook for fetching all bounties (admin, no status filter)
 */
export const useAllBounties = () => {
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'bounties'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setBounties(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching bounties:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { bounties, loading };
};

/**
 * Hook for fetching all claims (admin)
 */
export const useAllClaims = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'claims'), orderBy('submittedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setClaims(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching claims:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { claims, loading };
};

/**
 * Hook for fetching all arbitration cases (admin)
 */
export const useAllArbitration = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'arbitration'), orderBy('initiatedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setCases(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching arbitration:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { cases, loading };
};

/**
 * Hook for fetching all support tickets (admin)
 */
export const useAllSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'support_tickets'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setTickets(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching support tickets:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { tickets, loading };
};

/**
 * Hook for fetching all payment issues (admin)
 */
export const useAllPaymentIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'payment_issues'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setIssues(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching payment issues:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { issues, loading };
};

/**
 * Hook for fetching all transactions (admin)
 */
export const useAllTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setTransactions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching transactions:', err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  return { transactions, loading };
};

/**
 * Toggle admin status for a user
 */
export const toggleAdminStatus = async (userId, isAdmin) => {
  await updateDoc(doc(db, 'users', userId), {
    isAdmin,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Ban a user
 */
export const banUser = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    isBanned: true,
    bannedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Unban a user
 */
export const unbanUser = async (userId) => {
  await updateDoc(doc(db, 'users', userId), {
    isBanned: false,
    bannedAt: null,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a user's Firestore document
 * Note: Firebase Auth account deletion requires a Cloud Function
 */
export const deleteUserAccount = async (userId) => {
  await deleteDoc(doc(db, 'users', userId));
};

/**
 * Admin delete a bounty — also reject any pending claims and notify claimants
 */
export const adminDeleteBounty = async (bountyId) => {
  const bountySnap = await getDoc(doc(db, 'bounties', bountyId));
  if (!bountySnap.exists()) return;

  // Reject any pending claims
  const claimsSnap = await getDocs(
    query(collection(db, 'claims'), where('bountyId', '==', bountyId), where('status', '==', 'pending'))
  );
  for (const claimDoc of claimsSnap.docs) {
    await updateDoc(claimDoc.ref, {
      status: 'rejected',
      reviewedAt: serverTimestamp(),
    });
  }

  // Delete the bounty
  await deleteDoc(doc(db, 'bounties', bountyId));
};

/**
 * Resolve a dispute — approve or reject the claim, update arbitration status
 */
export const resolveDispute = async (arbitrationId, resolution, claimId, bountyId, rating) => {
  if (resolution === 'approve') {
    await approveClaim(claimId, bountyId, rating || 3, 'Resolved by admin arbitration');
  } else {
    await rejectClaim(claimId);
  }

  await updateDoc(doc(db, 'arbitration', arbitrationId), {
    status: 'resolved',
    resolution,
    resolvedAt: serverTimestamp(),
  });
};

/**
 * Create a payment issue
 */
export const createPaymentIssue = async (data) => {
  const docRef = await addDoc(collection(db, 'payment_issues'), {
    ...data,
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Update a payment issue
 */
export const updatePaymentIssue = async (issueId, updates) => {
  await updateDoc(doc(db, 'payment_issues', issueId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Update a support ticket status (admin)
 */
export const updateTicketStatus = async (ticketId, status) => {
  await updateDoc(doc(db, 'support_tickets', ticketId), {
    status,
    updatedAt: serverTimestamp(),
  });
};
